import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { seoAPI } from '../../services/api';
import { Card, Button } from '../ui';
import { handleApiError, showToast } from '../../utils/helpers';
import { X, Search } from 'lucide-react';

/**
 * Modal to create a new SEO plan (or view details when plan is passed).
 * Props: clientId, clientName, isOpen, onClose, onSuccess(plan), plan (optional - for view mode)
 */
const SeoPlanFormModal = ({ clientId, clientName, isOpen, onClose, onSuccess, plan: existingPlan }) => {
  const { isRTL } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');

  const isViewMode = !!existingPlan;

  useEffect(() => {
    if (isOpen) {
      if (existingPlan) {
        setName(existingPlan.name || '');
        setMonth(existingPlan.month ? String(existingPlan.month) : '');
        setYear(existingPlan.year ? String(existingPlan.year) : '');
      } else {
        setName('');
        setMonth('');
        setYear('');
      }
    }
  }, [isOpen, existingPlan]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      showToast.error(isRTL ? 'يرجى إدخال اسم الخطة' : 'Please enter a plan name');
      return;
    }
    try {
      setLoading(true);
      const payload = { name: name.trim() };
      if (month) payload.month = parseInt(month, 10);
      if (year) payload.year = parseInt(year, 10);
      const res = await seoAPI.createPlan(clientId, payload);
      showToast.success(isRTL ? 'تم إنشاء خطة SEO' : 'SEO plan created');
      onSuccess?.(res.data?.plan);
      onClose();
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" dir={isRTL ? 'rtl' : 'ltr'}>
      <Card className="w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <span className="flex w-9 h-9 items-center justify-center rounded-lg bg-amber-100 text-amber-600">
              <Search className="w-4 h-4" />
            </span>
            <h3 className="text-lg font-semibold text-slate-900">
              {isViewMode
                ? (isRTL ? 'تفاصيل الخطة' : 'Plan details')
                : (isRTL ? 'إنشاء خطة SEO' : 'Create SEO plan')}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {clientName && (
          <p className="px-4 pt-2 text-sm text-slate-600">
            {isRTL ? 'العميل:' : 'Client:'} <span className="font-medium text-slate-800">{clientName}</span>
          </p>
        )}

        {isViewMode ? (
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">{isRTL ? 'اسم الخطة' : 'Plan name'}</label>
              <p className="text-slate-900 font-medium">{existingPlan.name}</p>
            </div>
            {(existingPlan.month || existingPlan.year) && (
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">{isRTL ? 'الشهر / السنة' : 'Month / Year'}</label>
                <p className="text-slate-700">
                  {existingPlan.month ? `${existingPlan.month} / ` : ''}{existingPlan.year || '-'}
                </p>
              </div>
            )}
            <div className="flex justify-end pt-4">
              <Button variant="secondary" onClick={onClose}>
                {isRTL ? 'إغلاق' : 'Close'}
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                {isRTL ? 'اسم الخطة' : 'Plan name'} *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                placeholder={isRTL ? 'مثال: خطة SEO مارس 2026' : 'e.g. SEO Plan March 2026'}
                autoFocus
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  {isRTL ? 'الشهر' : 'Month'}
                </label>
                <input
                  type="number"
                  min="1"
                  max="12"
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                  placeholder="3"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  {isRTL ? 'السنة' : 'Year'}
                </label>
                <input
                  type="number"
                  min="2000"
                  max="2100"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                  placeholder="2026"
                />
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-4 border-t border-slate-200">
              <Button type="button" variant="secondary" onClick={onClose}>
                {isRTL ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button type="submit" loading={loading}>
                {isRTL ? 'إنشاء الخطة' : 'Create plan'}
              </Button>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
};

export default SeoPlanFormModal;
